/**
 * Invoice totals — the dashboard mirror of infrastructure/lambda/invoices/
 * invoiceMath.mjs (D-INV-2). Used for the live preview only; the server's
 * figures are authoritative and replace these after every save. Both must
 * pass math-vectors.json (copied here from infrastructure/docs/invoicing;
 * a test fails if the copies drift). The algorithm is CONTRACT.md §7.
 *
 * Money is integer minor units; divisions round half up in BigInt so no
 * float touches money. Quantity is converted to thousandths first.
 */

export interface MathDiscount { type: 'AMOUNT' | 'PERCENT'; value: number }
export interface MathLine { quantity: number; unitPriceMinor: number; discount?: MathDiscount | null; taxRateBp?: number }
export interface MathCharge { amountMinor: number; taxable: boolean }
export interface MathInput {
  lines: MathLine[];
  invoiceDiscount?: MathDiscount | null;
  charges?: MathCharge[];
  defaultTaxRateBp?: number;
  taxInclusive?: boolean;
  roundToWholeUnit?: boolean;
  depositAppliedMinor?: number;
  paidMinor?: number;
  creditedMinor?: number;
}
export interface MathTaxLine { rateBp: number; baseMinor: number; taxMinor: number }
export interface MathResult {
  lineTotalsMinor: number[];
  lineDiscountsMinor: number[];
  subtotalMinor: number;
  discountMinor: number;
  chargesMinor: number;
  taxLines: MathTaxLine[];
  taxMinor: number;
  roundingMinor: number;
  totalMinor: number;
  balanceMinor: number;
  amountInWords: string;
}

export function divRound(num: number, den: number): number {
  const n = BigInt(Math.trunc(num));
  const d = BigInt(Math.trunc(den));
  return Number((n * BigInt(2) + d) / (BigInt(2) * d));
}

export const qtyThousandths = (quantity: number) => Math.round(Number(quantity || 0) * 1000);

export function discountOf(discount: MathDiscount | null | undefined, base: number): number {
  if (!discount || !discount.value) return 0;
  if (discount.type === 'PERCENT') return divRound(base * Math.min(discount.value, 10000), 10000);
  return Math.min(discount.value, base);
}

export function allocate(amount: number, weights: number[]): number[] {
  const total = weights.reduce((a, w) => a + w, 0);
  const parts = weights.map(() => 0);
  if (!amount || !total) return parts;
  const raw = weights.map((w, k) => {
    const p = BigInt(w) * BigInt(amount);
    return { k, floor: Number(p / BigInt(total)), rem: Number(p % BigInt(total)) };
  });
  raw.forEach((r) => { parts[r.k] = r.floor; });
  let left = amount - raw.reduce((a, r) => a + r.floor, 0);
  [...raw].sort((a, b) => b.rem - a.rem || a.k - b.k).forEach((r) => {
    if (left > 0) { parts[r.k] += 1; left -= 1; }
  });
  return parts;
}

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function belowThousand(n: number): string {
  let s = '';
  if (n >= 100) {
    s += `${ONES[Math.floor(n / 100)]} hundred`;
    n %= 100;
    if (n) s += ' and ';
  }
  if (n >= 20) {
    s += TENS[Math.floor(n / 10)];
    if (n % 10) s += `-${ONES[n % 10]}`;
  } else if (n) {
    s += ONES[n];
  }
  return s;
}

export function amountInWords(minor: number): string {
  const rupees = Math.floor(minor / 100);
  const cents = minor % 100;
  const parts: string[] = [];
  let n = rupees;
  ([[1e9, 'billion'], [1e6, 'million'], [1e3, 'thousand']] as [number, string][]).forEach(([v, label]) => {
    if (n >= v) {
      parts.push(`${belowThousand(Math.floor(n / v))} ${label}`);
      n %= v;
    }
  });
  if (n) parts.push(belowThousand(n));
  const s = `Rupees ${parts.join(' ') || 'zero'}${cents ? ` and cents ${belowThousand(cents)}` : ''} only`;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function computeTotals(input: MathInput): MathResult {
  const lines = (input.lines || []).map((l) => {
    const gross = divRound(qtyThousandths(l.quantity) * (l.unitPriceMinor || 0), 1000);
    const discountMinor = discountOf(l.discount, gross);
    return { gross, discountMinor, net: gross - discountMinor, rate: l.taxRateBp || 0 };
  });

  const subtotalMinor = lines.reduce((a, l) => a + l.gross, 0);
  const afterLines = lines.reduce((a, l) => a + l.net, 0);
  const invoiceDiscountMinor = discountOf(input.invoiceDiscount, afterLines);
  const allocation = allocate(invoiceDiscountMinor, lines.map((l) => l.net));
  const lineTotalsMinor = lines.map((l, k) => l.net - allocation[k]);

  const discountMinor = lines.reduce((a, l) => a + l.discountMinor, 0) + invoiceDiscountMinor;
  const charges = input.charges || [];
  const chargesMinor = charges.reduce((a, c) => a + (c.amountMinor || 0), 0);

  const bases = new Map<number, number>();
  const addBase = (rate: number, amount: number) => bases.set(rate, (bases.get(rate) || 0) + amount);
  lines.forEach((l, k) => { if (l.rate > 0) addBase(l.rate, lineTotalsMinor[k]); });
  if ((input.defaultTaxRateBp || 0) > 0) {
    charges.forEach((c) => { if (c.taxable) addBase(input.defaultTaxRateBp as number, c.amountMinor || 0); });
  }

  const taxLines = Array.from(bases.entries())
    .filter(([, base]) => base > 0)
    .sort((a, b) => b[0] - a[0])
    .map(([rateBp, base]) => {
      if (input.taxInclusive) {
        const taxMinor = divRound(base * rateBp, 10000 + rateBp);
        return { rateBp, baseMinor: base - taxMinor, taxMinor };
      }
      return { rateBp, baseMinor: base, taxMinor: divRound(base * rateBp, 10000) };
    });
  const taxMinor = taxLines.reduce((a, t) => a + t.taxMinor, 0);

  let totalMinor = afterLines - invoiceDiscountMinor + chargesMinor + (input.taxInclusive ? 0 : taxMinor);
  let roundingMinor = 0;
  if (input.roundToWholeUnit) {
    const rounded = divRound(totalMinor, 100) * 100;
    roundingMinor = rounded - totalMinor;
    totalMinor = rounded;
  }
  const balanceMinor = totalMinor - (input.depositAppliedMinor || 0) - (input.paidMinor || 0) - (input.creditedMinor || 0);

  return {
    lineTotalsMinor, lineDiscountsMinor: lines.map((l) => l.discountMinor),
    subtotalMinor, discountMinor, chargesMinor, taxLines, taxMinor, roundingMinor,
    totalMinor, balanceMinor, amountInWords: amountInWords(totalMinor),
  };
}
